import { Button } from '@sequencehq/core-components'
import { useAtom, useAtomValue } from 'jotai'
import { scheduleCurrencyAtom } from '../../../atoms/reviewPricing.atoms'
import { ReactNode, useState } from 'react'
import NotFound from 'components/ErrorPages/NotFound'
import PageError from 'components/ErrorPages/PageError'
import {
  VariantPriceLayoutContent,
  useVariantPriceLayout,
  PricingEditorProvider,
  EditorMode,
  PricingEditorPortErrors,
  usePricingEditorRoot
} from 'common/drawers/PricingEditor'
import invariant from 'tiny-invariant'
import { pricingDetailsAtom } from '../../../atoms/reviewPricing.primitives.atoms'
import { getPricesForPhase, updatePhase } from '../../../utils/phases'
import { applyNewPriceToPhases } from '@sequencehq/utils'
import {
  pricingUtilsDiscountAdapter,
  pricingUtilsMinimumAdapter,
  pricingUtilsPhaseAdapter
} from '../reviewPricingDrawers.adapters'
import { Outlet } from 'react-router-dom'
import { StepDrawer } from '../../../../components/StepDrawer'

type Props = {
  phaseId: string
  priceId: string | undefined
  onDismiss: () => void
}

export const PriceEditorDrawer = ({
  phaseId,
  priceId: originalPriceId,
  onDismiss
}: Props) => {
  const [updatedPriceId, setUpdatedPriceId] = useState<string | undefined>()
  const priceId = updatedPriceId ?? originalPriceId

  const currency = useAtomValue(scheduleCurrencyAtom)
  const [pricingDetails, setPricingDetails] = useAtom(pricingDetailsAtom)

  const phase = pricingDetails.phases.find(phase => phase.id === phaseId)
  invariant(phase, 'phase is undefined')

  const allPricesForPhase = getPricesForPhase({
    phase,
    prices: pricingDetails.prices
  })

  const existingPrice = allPricesForPhase.find(price => price.id === priceId)

  const productIdsUsedInPhase = allPricesForPhase
    .map(price => price.productId)
    .filter((id): id is string => Boolean(id))

  const { domain, ready, error } = usePricingEditorRoot({
    variant: 'variant',
    initialMode: existingPrice ? EditorMode.EDIT : EditorMode.CREATE,
    currency,
    variantPriceId: existingPrice?.id,
    onSave: ({ price: updatedPrice }) => {
      /**
       * Only the phase being edited is passed, so the update stops there.
       */
      const updates = applyNewPriceToPhases({
        originalPriceId,
        newPrice: updatedPrice,
        phaseId,
        phases: [
          pricingUtilsPhaseAdapter.in({ phase, prices: pricingDetails.prices })
        ]
      })

      setPricingDetails({
        phases: updatePhase({
          phases: pricingDetails.phases,
          phaseId,
          update: phaseToUpdate => ({
            ...phaseToUpdate,
            priceIds:
              updates.updatedPhases[phaseId]?.priceIds ??
              phaseToUpdate.priceIds,
            discounts: phaseToUpdate.discounts.map(discount => {
              const updatedDiscount = updates.updatedDiscounts[discount.id]
              if (!updatedDiscount) return discount
              return pricingUtilsDiscountAdapter.out(
                updatedDiscount,
                discount.boundingBoxIndex
              )
            }),
            minimums: phaseToUpdate.minimums.map(minimum => {
              const updatedMinimum = updates.updatedMinimums[minimum.id]
              if (!updatedMinimum) return minimum
              return pricingUtilsMinimumAdapter.out(updatedMinimum)
            })
          })
        }),
        prices: [...pricingDetails.prices, updatedPrice]
      })

      // use the new price id to prevent crash before an exit transition completes
      setUpdatedPriceId(updatedPrice.id)

      onDismiss()
    },
    existingPrice: existingPrice
      ? {
          ...existingPrice,
          productId: existingPrice.productId ?? '',
          customMetricParameters: []
        }
      : undefined,
    existingProduct: existingPrice?.productId
      ? { id: existingPrice.productId, name: '' }
      : undefined,
    disableDefaultPricePorts: true,
    disableDefaultProductPorts: true,
    productIdsUsedInPhase,
    showProductSelectField: true
  })

  if (error) {
    return (
      <StepDrawer
        data-testid="reviewPricing.drawers.priceEditor.error"
        title="Pricing information"
        onClose={onDismiss}
        footer={
          <Button variant="secondary" onClick={onDismiss}>
            Close
          </Button>
        }
      >
        {error === PricingEditorPortErrors.NotFound ? (
          <NotFound />
        ) : (
          <PageError />
        )}
      </StepDrawer>
    )
  }

  return (
    <PricingEditorProvider value={domain}>
      <PriceEditorDrawerContent
        loading={!ready}
        variantPriceId={existingPrice?.id}
        onCancel={onDismiss}
      />
      <Outlet />
    </PricingEditorProvider>
  )
}

const PriceEditorDrawerContent = (props: {
  loading: boolean
  variantPriceId?: string
  onCancel?: () => void
  drawerLeadingContent?: ReactNode
}) => {
  const {
    isListPriceBasedVariant,
    pricingModel,
    onCancel,
    submit,
    submitting
  } = useVariantPriceLayout({
    variantPriceId: props.variantPriceId,
    onCancel: props.onCancel
  })

  return (
    <StepDrawer
      data-testid="reviewPricing.drawers.priceEditor"
      title="Pricing information"
      onClose={onCancel}
      footer={
        <>
          <Button
            variant="secondary"
            onClick={onCancel}
            data-testid="reviewPricing.drawers.priceEditor.cancel"
          >
            Cancel
          </Button>
          <Button
            data-testid="reviewPricing.drawers.priceEditor.save"
            variant="primary"
            onClick={submit.action}
            disabled={submitting || submit.disabled}
          >
            Save price
          </Button>
        </>
      }
    >
      <VariantPriceLayoutContent
        loading={props.loading}
        pricingModel={pricingModel}
        isListPriceBasedVariant={isListPriceBasedVariant}
        showTitle={false}
      />
    </StepDrawer>
  )
}
